import { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, ArrowUpRight, ArrowLeft, Calendar as CalendarIcon, Clock, Zap } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import Reveal from "@/components/site/Reveal";
import SectionLabel from "@/components/site/SectionLabel";
import UrgencyRail from "@/components/site/UrgencyRail";
import { API } from "@/lib/site";

const AGE_RANGES = ["18–24", "25–29", "30–34", "35–39", "40–45", "46+"];
const SITUATIONS = [
    "Single & rebuilding",
    "In a dry spell / no recent dates",
    "Dating but not the women I want",
    "Recently out of a long-term relationship",
    "High-performer in career, stuck in dating",
    "Other",
];

const fieldClass =
    "w-full bg-[#060c1e] border border-[#1a2a4a] focus:border-[#1E90FF] focus:ring-1 focus:ring-[#1E90FF] outline-none text-white placeholder-white/30 px-4 py-3.5 transition-colors";

const STEP_ITEMS = [
    { n: 1, label: "Your Info" },
    { n: 2, label: "Pick a Time" },
];

function StepIndicator({ step }) {
    return (
        <div
            data-testid="step-indicator"
            className="flex items-center gap-3 sm:gap-4"
        >
            {STEP_ITEMS.map((s, i) => {
                const active = s.n === step;
                const done = s.n < step;
                return (
                    <div key={s.n} className="flex items-center gap-3 sm:gap-4">
                        <div className="flex items-center gap-2.5">
                            <span
                                className={`w-7 h-7 inline-flex items-center justify-center font-display font-black text-xs border transition-all ${
                                    active
                                        ? "bg-[#1E90FF] text-white border-[#1E90FF] shadow-[0_0_18px_rgba(30,144,255,0.5)]"
                                        : done
                                        ? "bg-[#0a1429] text-[#00B7FF] border-[#1E90FF]"
                                        : "bg-transparent text-white/40 border-[#1a2a4a]"
                                }`}
                            >
                                {done ? "✓" : s.n}
                            </span>
                            <span
                                className={`text-[11px] sm:text-xs uppercase tracking-[0.18em] font-semibold ${
                                    active || done
                                        ? "text-white"
                                        : "text-white/40"
                                }`}
                            >
                                {s.label}
                            </span>
                        </div>
                        {i < STEP_ITEMS.length - 1 && (
                            <span
                                className={`w-8 sm:w-12 h-px ${
                                    done ? "bg-[#1E90FF]" : "bg-[#1a2a4a]"
                                }`}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

const isoDate = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

const formatSlot = (hhmm) => {
    const [h, m] = hhmm.split(":").map(Number);
    const period = h >= 12 ? "PM" : "AM";
    const hour12 = ((h + 11) % 12) + 1;
    return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
};

export default function Book() {
    useEffect(() => {
        document.title = "Book a Free Consultation | 90 Day Alpha";
    }, []);

    const [step, setStep] = useState(1);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        age_range: "",
        current_situation: "",
        biggest_challenge: "",
        commitment: false,
    });

    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState("");
    const [availability, setAvailability] = useState({
        loading: false,
        slots: [],
        booked: [],
        error: "",
    });

    const timezoneName = useMemo(() => {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
        } catch {
            return "UTC";
        }
    }, []);

    const update = (k) => (e) => {
        const v =
            e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((f) => ({ ...f, [k]: v }));
    };

    const goStep2 = (e) => {
        e?.preventDefault();
        if (
            !form.name.trim() ||
            !form.email.trim() ||
            !form.phone.trim() ||
            !form.age_range ||
            !form.current_situation ||
            !form.biggest_challenge.trim()
        ) {
            toast.error("Please complete every field before continuing.");
            return;
        }
        setStep(2);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const fetchAvailability = useCallback(async (date) => {
        setAvailability({ loading: true, slots: [], booked: [], error: "" });
        try {
            const res = await axios.get(`${API}/availability`, {
                params: { date: isoDate(date) },
            });
            setAvailability({
                loading: false,
                slots: res.data.available_slots || [],
                booked: res.data.booked_slots || [],
                error: "",
            });
        } catch (err) {
            setAvailability({
                loading: false,
                slots: [],
                booked: [],
                error: "Could not load availability. Try another date.",
            });
        }
    }, []);

    const handleDateChange = (date) => {
        if (!date) return;
        setSelectedDate(date);
        setSelectedTime("");
        fetchAvailability(date);
    };

    const handleSubmit = async () => {
        if (!selectedDate) {
            toast.error("Please pick a date first.");
            return;
        }
        if (!selectedTime) {
            toast.error("Please choose a time slot.");
            return;
        }
        try {
            setSubmitting(true);
            await axios.post(`${API}/consultations`, {
                ...form,
                scheduled_date: isoDate(selectedDate),
                scheduled_time: selectedTime,
                timezone_name: timezoneName,
            });
            setSubmitted(true);
            toast.success("Your consultation is locked in. See you there.");
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                "Could not book. Try again or pick a different slot.";
            toast.error(typeof detail === "string" ? detail : "Submission failed.");
            if (err?.response?.status === 409 && selectedDate) {
                fetchAvailability(selectedDate);
                setSelectedTime("");
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div
                data-testid="book-success-page"
                className="min-h-[100vh] electric-bg flex items-center justify-center px-6 py-32 relative overflow-hidden"
            >
                <div
                    aria-hidden
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-[#1E90FF]/15 blur-[160px] animate-pulse-glow pointer-events-none"
                />
                <div className="relative max-w-2xl text-center">
                    <Reveal>
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#1E90FF]/10 border border-[#1E90FF]/40 mb-2 glow-blue">
                            <CheckCircle2
                                size={40}
                                className="text-[#00B7FF]"
                                strokeWidth={1.5}
                            />
                        </div>
                        <p className="mt-8 text-xs uppercase tracking-[0.3em] font-semibold text-[#00B7FF]">
                            Consultation Locked In
                        </p>
                        <h1 className="mt-6 font-display font-black text-white text-4xl md:text-6xl tracking-tighter leading-[1.02]">
                            You took the step.{" "}
                            <span className="text-[#D4AF37]">Now we move.</span>
                        </h1>
                        <div className="mt-8 inline-flex items-center gap-3 px-5 py-3 border border-[#1a2a4a] bg-[#060c1e]">
                            <CalendarIcon size={18} className="text-[#00B7FF]" />
                            <span
                                data-testid="success-scheduled-text"
                                className="text-white font-display font-bold tracking-tight"
                            >
                                {selectedDate
                                    ? selectedDate.toLocaleDateString(undefined, {
                                          weekday: "long",
                                          month: "long",
                                          day: "numeric",
                                      })
                                    : ""}
                                {" · "}
                                {selectedTime ? formatSlot(selectedTime) : ""}
                                {" · "}
                                <span className="text-white/60 font-normal text-sm">
                                    {timezoneName}
                                </span>
                            </span>
                        </div>
                        <p className="mt-8 text-white/75 leading-relaxed text-base sm:text-lg">
                            Calendar invite + Zoom link are landing in your
                            inbox shortly. Check spam if you don’t see it. Show
                            up ready to be honest about where you’re stuck.
                        </p>
                        <div className="mt-12 flex flex-col sm:flex-row gap-4 items-center justify-center">
                            <Link
                                to="/"
                                data-testid="success-home-link"
                                className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-[0.18em] text-xs hover:bg-[#C5A059] transition-all"
                            >
                                Back to Home <ArrowUpRight size={16} />
                            </Link>
                            <Link
                                to="/program"
                                data-testid="success-program-link"
                                className="text-white/70 hover:text-[#00B7FF] uppercase tracking-[0.2em] text-xs font-semibold"
                            >
                                Read about the program →
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="book-page" className="electric-bg relative overflow-hidden">
            <div
                aria-hidden
                className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-[#1E90FF]/10 blur-[160px] pointer-events-none"
            />

            <section className="relative pt-32 pb-10 md:pt-40 md:pb-14">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Reveal>
                        <SectionLabel testId="book-label">
                            Free 30-Minute Consultation
                        </SectionLabel>
                        <h1 className="mt-6 font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.98] max-w-3xl">
                            Book your{" "}
                            <span className="text-[#00B7FF] text-glow-blue">
                                consultation
                            </span>
                            .
                        </h1>
                        <p className="mt-5 text-white/75 leading-relaxed text-base max-w-2xl">
                            Two steps. Tell us where you’re stuck, then lock in
                            a time. We’ll meet you on Zoom.
                        </p>
                        <div className="mt-8">
                            <StepIndicator step={step} />
                        </div>
                        <UrgencyRail />
                    </Reveal>
                </div>
            </section>

            <section className="relative pb-32">
                <div className="max-w-4xl mx-auto px-6 md:px-12">
                    {step === 1 && (
                        <form
                            data-testid="booking-form"
                            onSubmit={goStep2}
                            className="bg-[#060c1e] border border-[#1a2a4a] p-6 md:p-10 space-y-6"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label
                                        htmlFor="name"
                                        className="block text-xs uppercase tracking-[0.2em] font-semibold text-white/70 mb-2"
                                    >
                                        Full Name *
                                    </label>
                                    <input
                                        id="name"
                                        type="text"
                                        data-testid="form-name"
                                        className={fieldClass}
                                        placeholder="Your full name"
                                        value={form.name}
                                        onChange={update("name")}
                                        required
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="email"
                                        className="block text-xs uppercase tracking-[0.2em] font-semibold text-white/70 mb-2"
                                    >
                                        Email *
                                    </label>
                                    <input
                                        id="email"
                                        type="email"
                                        data-testid="form-email"
                                        className={fieldClass}
                                        placeholder="you@example.com"
                                        value={form.email}
                                        onChange={update("email")}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label
                                        htmlFor="phone"
                                        className="block text-xs uppercase tracking-[0.2em] font-semibold text-white/70 mb-2"
                                    >
                                        Phone (with country code) *
                                    </label>
                                    <input
                                        id="phone"
                                        type="tel"
                                        data-testid="form-phone"
                                        className={fieldClass}
                                        placeholder="+1 555 555 5555"
                                        value={form.phone}
                                        onChange={update("phone")}
                                        required
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="age_range"
                                        className="block text-xs uppercase tracking-[0.2em] font-semibold text-white/70 mb-2"
                                    >
                                        Age Range *
                                    </label>
                                    <select
                                        id="age_range"
                                        data-testid="form-age"
                                        className={fieldClass}
                                        value={form.age_range}
                                        onChange={update("age_range")}
                                        required
                                    >
                                        <option value="" disabled>
                                            Select your age range
                                        </option>
                                        {AGE_RANGES.map((a) => (
                                            <option
                                                key={a}
                                                value={a}
                                                className="bg-[#060c1e]"
                                            >
                                                {a}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label
                                    htmlFor="current_situation"
                                    className="block text-xs uppercase tracking-[0.2em] font-semibold text-white/70 mb-2"
                                >
                                    Current Situation *
                                </label>
                                <select
                                    id="current_situation"
                                    data-testid="form-situation"
                                    className={fieldClass}
                                    value={form.current_situation}
                                    onChange={update("current_situation")}
                                    required
                                >
                                    <option value="" disabled>
                                        Best description of where you are
                                    </option>
                                    {SITUATIONS.map((s) => (
                                        <option
                                            key={s}
                                            value={s}
                                            className="bg-[#060c1e]"
                                        >
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label
                                    htmlFor="biggest_challenge"
                                    className="block text-xs uppercase tracking-[0.2em] font-semibold text-white/70 mb-2"
                                >
                                    Biggest Challenge Right Now *
                                </label>
                                <textarea
                                    id="biggest_challenge"
                                    data-testid="form-challenge"
                                    rows={5}
                                    className={fieldClass + " resize-none"}
                                    placeholder="Be specific. What’s the real bottleneck in your dating life or self-image?"
                                    value={form.biggest_challenge}
                                    onChange={update("biggest_challenge")}
                                    required
                                />
                            </div>

                            <label className="flex items-start gap-3 pt-2 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    data-testid="form-commitment"
                                    checked={form.commitment}
                                    onChange={update("commitment")}
                                    className="mt-1 w-5 h-5 accent-[#1E90FF] bg-[#020617] border border-[#1a2a4a] cursor-pointer"
                                />
                                <span className="text-sm text-white/75 leading-relaxed group-hover:text-white transition-colors">
                                    I understand 90 Day Alpha is a premium
                                    coaching program and I’m ready to invest the
                                    time, focus, and resources required to
                                    transform.
                                </span>
                            </label>

                            <div className="pt-4">
                                <button
                                    type="submit"
                                    data-testid="form-next"
                                    className="inline-flex items-center justify-center gap-2 w-full md:w-auto bg-[#D4AF37] text-black font-bold uppercase tracking-[0.18em] text-xs px-10 py-4 hover:bg-[#C5A059] transition-all shadow-[0_0_40px_rgba(212,175,55,0.25)]"
                                >
                                    Continue To Calendar{" "}
                                    <ArrowUpRight size={16} />
                                </button>
                            </div>
                        </form>
                    )}

                    {step === 2 && (
                        <div
                            data-testid="booking-calendar-step"
                            className="bg-[#060c1e] border border-[#1a2a4a] p-6 md:p-10"
                        >
                            <button
                                type="button"
                                data-testid="back-to-info"
                                onClick={() => setStep(1)}
                                className="inline-flex items-center gap-2 text-white/60 hover:text-[#00B7FF] text-xs uppercase tracking-[0.18em] font-semibold mb-6 transition-colors"
                            >
                                <ArrowLeft size={14} /> Back to your info
                            </button>

                            <div className="flex items-center gap-2 mb-2">
                                <Zap
                                    size={14}
                                    className="text-[#00B7FF]"
                                    fill="currentColor"
                                />
                                <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#00B7FF]">
                                    Pick a Time That Works
                                </p>
                            </div>
                            <h2 className="font-display font-black text-white text-2xl md:text-3xl tracking-tight">
                                Choose your{" "}
                                <span className="text-[#D4AF37]">date</span> &
                                slot
                            </h2>
                            <p className="mt-2 text-white/65 text-sm">
                                Weekdays · 30-minute slots · Your timezone:{" "}
                                <span className="text-white">
                                    {timezoneName}
                                </span>
                            </p>

                            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
                                <div
                                    data-testid="calendar-wrapper"
                                    className="bg-[#020617] border border-[#1a2a4a] p-4 flex items-start justify-center"
                                >
                                    <Calendar
                                        mode="single"
                                        selected={selectedDate}
                                        onSelect={handleDateChange}
                                        disabled={(d) => {
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            const max = new Date();
                                            max.setDate(today.getDate() + 60);
                                            return (
                                                d < today ||
                                                d > max ||
                                                d.getDay() === 0 ||
                                                d.getDay() === 6
                                            );
                                        }}
                                        classNames={{
                                            caption_label:
                                                "text-white font-display font-bold text-base",
                                            head_cell:
                                                "text-[#00B7FF] rounded-md w-9 font-semibold text-[10px] uppercase tracking-widest",
                                            day: "h-9 w-9 p-0 font-medium text-white/85 hover:bg-[#0a1429] hover:text-[#00B7FF] rounded-none transition-colors",
                                            day_selected:
                                                "bg-[#1E90FF] text-white hover:bg-[#1E90FF] hover:text-white focus:bg-[#1E90FF] focus:text-white shadow-[0_0_24px_rgba(30,144,255,0.6)]",
                                            day_today:
                                                "border border-[#1E90FF]/50 text-[#00B7FF]",
                                            day_disabled:
                                                "text-white/15 hover:bg-transparent hover:text-white/15 cursor-not-allowed",
                                            day_outside:
                                                "text-white/20",
                                            nav_button:
                                                "h-7 w-7 bg-transparent border border-[#1a2a4a] text-white/70 hover:bg-[#0a1429] hover:text-[#00B7FF]",
                                            cell: "p-0 text-center",
                                        }}
                                    />
                                </div>

                                <div data-testid="slots-wrapper">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Clock
                                            size={16}
                                            className="text-[#00B7FF]"
                                        />
                                        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-white/70">
                                            {selectedDate
                                                ? selectedDate.toLocaleDateString(
                                                      undefined,
                                                      {
                                                          weekday: "long",
                                                          month: "short",
                                                          day: "numeric",
                                                      }
                                                  )
                                                : "Select a date to see slots"}
                                        </p>
                                    </div>

                                    {!selectedDate && (
                                        <div className="bg-[#020617] border border-dashed border-[#1a2a4a] p-8 text-center text-white/45 text-sm">
                                            Pick a date on the left to see
                                            available 30-minute slots.
                                        </div>
                                    )}

                                    {selectedDate && availability.loading && (
                                        <div className="flex items-center justify-center py-12 text-white/60">
                                            <Loader2
                                                size={20}
                                                className="animate-spin mr-2"
                                            />{" "}
                                            Loading slots…
                                        </div>
                                    )}

                                    {selectedDate &&
                                        !availability.loading &&
                                        availability.error && (
                                            <div className="bg-[#1a0707] border border-[#8B0000]/60 p-4 text-sm text-[#fca5a5]">
                                                {availability.error}
                                            </div>
                                        )}

                                    {selectedDate &&
                                        !availability.loading &&
                                        !availability.error &&
                                        availability.slots.length === 0 && (
                                            <div className="bg-[#020617] border border-[#1a2a4a] p-6 text-sm text-white/60">
                                                No slots left for this day. Pick
                                                another date.
                                            </div>
                                        )}

                                    {selectedDate &&
                                        !availability.loading &&
                                        availability.slots.length > 0 && (
                                            <div
                                                data-testid="slot-grid"
                                                className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[360px] overflow-y-auto pr-1"
                                            >
                                                {availability.slots.map((s) => {
                                                    const active =
                                                        selectedTime === s;
                                                    return (
                                                        <button
                                                            key={s}
                                                            type="button"
                                                            data-testid={`slot-${s}`}
                                                            onClick={() =>
                                                                setSelectedTime(s)
                                                            }
                                                            className={`px-3 py-3 text-sm font-display font-semibold tracking-tight border transition-all ${
                                                                active
                                                                    ? "bg-[#1E90FF] text-white border-[#1E90FF] shadow-[0_0_24px_rgba(30,144,255,0.5)]"
                                                                    : "bg-[#020617] text-white/85 border-[#1a2a4a] hover:border-[#1E90FF] hover:text-[#00B7FF]"
                                                            }`}
                                                        >
                                                            {formatSlot(s)}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}

                                    <button
                                        type="button"
                                        data-testid="confirm-booking"
                                        onClick={handleSubmit}
                                        disabled={
                                            submitting ||
                                            !selectedDate ||
                                            !selectedTime
                                        }
                                        className="mt-6 inline-flex items-center justify-center gap-2 w-full bg-[#D4AF37] text-black font-bold uppercase tracking-[0.18em] text-xs px-10 py-4 hover:bg-[#C5A059] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_40px_rgba(212,175,55,0.25)]"
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2
                                                    size={16}
                                                    className="animate-spin"
                                                />{" "}
                                                Booking…
                                            </>
                                        ) : (
                                            <>
                                                Confirm{" "}
                                                {selectedTime
                                                    ? formatSlot(selectedTime)
                                                    : "Booking"}{" "}
                                                <ArrowUpRight size={16} />
                                            </>
                                        )}
                                    </button>
                                    <p className="mt-3 text-[11px] text-white/45 text-center">
                                        We’ll send a calendar invite + Zoom link
                                        to your email.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
